import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { SUBMIT_COURSE_TEST } from '@/lib/graphql/mutations/course';
import { CUSTOMER_COURSE_TEST_ATTEMPTS } from '@/lib/graphql/queries/course';
import { useToast } from '@/hooks/use-toast';

interface TestOption {
  id: string;
  text: string;
}

interface TestQuestion {
  id: string;
  prompt: string;
  type: 'SINGLE_CHOICE' | 'TRUE_FALSE';
  options: TestOption[];
  order: number;
  points: number;
}

interface CourseTest {
  id: string;
  required: boolean;
  passScore: number;
  questions: TestQuestion[];
}

interface CourseTestDialogProps {
  courseId: string;
  test: CourseTest | null | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCompleted?: () => void;
  alreadyPassed?: boolean;
}

const CourseTestDialog = ({
  courseId,
  test,
  open,
  onOpenChange,
  onCompleted,
  alreadyPassed,
}: CourseTestDialogProps) => {
  const { toast } = useToast();
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<{
    passed: boolean;
    scorePercent: number;
    correctCount: number;
    totalCount: number;
    courseCompleted: boolean;
  } | null>(null);

  const [submitTest, { loading: submitting }] = useMutation(SUBMIT_COURSE_TEST, {
    refetchQueries: [
      { query: CUSTOMER_COURSE_TEST_ATTEMPTS, variables: { courseId } },
    ],
  });

  useEffect(() => {
    if (open) {
      setAnswers({});
      setResult(null);
    }
  }, [open, test?.id]);

  const sortedQuestions = useMemo(
    () =>
      test?.questions
        ? [...test.questions].sort((a, b) => a.order - b.order)
        : [],
    [test?.questions]
  );

  const allAnswered =
    sortedQuestions.length > 0 &&
    sortedQuestions.every((q) => !!answers[q.id]);

  if (!test || sortedQuestions.length === 0) return null;

  const handleSelect = (qid: string, oid: string) => {
    setAnswers((prev) => ({ ...prev, [qid]: oid }));
  };

  const handleSubmit = async () => {
    try {
      const payload = sortedQuestions.map((q) => ({
        questionId: q.id,
        selectedOptionId: answers[q.id],
      }));
      const res = await submitTest({
        variables: { courseId, answers: payload },
      });
      const data = res.data?.courseSubmitTest;
      if (!data) return;
      setResult({
        passed: data.passed,
        scorePercent: data.scorePercent,
        correctCount: data.correctCount,
        totalCount: data.totalCount,
        courseCompleted: data.courseCompleted,
      });
      if (data.passed) {
        toast({
          title: 'Амжилттай!',
          description: `Та шалгалтыг ${data.scorePercent}% -р давлаа.`,
        });
        onCompleted?.();
      } else {
        toast({
          title: 'Дахин оролдоно уу',
          description: `Таны оноо: ${data.scorePercent}% (${data.correctCount}/${data.totalCount})`,
          variant: 'destructive',
        });
      }
    } catch (err: any) {
      toast({
        title: 'Алдаа',
        description: err?.message || 'Шалгалт илгээхэд алдаа гарлаа',
        variant: 'destructive',
      });
    }
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-2xl max-h-[85vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>
            {result
              ? result.passed
                ? 'Баяр хүргэе!'
                : 'Дахин оролдоно уу'
              : 'Шалгалт'}
          </DialogTitle>
          <DialogDescription>
            {result
              ? result.passed
                ? `Та шалгалтыг амжилттай давлаа. Оноо: ${result.scorePercent}%`
                : `Та шалгалтыг давж чадсангүй. Оноо: ${result.scorePercent}% (${result.correctCount}/${result.totalCount})`
              : `Тэнцэх оноо: ${test.passScore}%`}
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className='flex flex-col items-center gap-4 py-6'>
            {result.passed ? (
              <CheckCircle2 className='h-16 w-16 text-green-500' />
            ) : (
              <XCircle className='h-16 w-16 text-red-500' />
            )}
            <p className='text-lg font-semibold'>
              {result.correctCount} / {result.totalCount} зөв хариулт
            </p>
            {!result.passed && (
              <p className='text-sm text-muted-foreground text-center'>
                Шалгалтыг дахин өгөхийн тулд видеог дахин төгсгөл хүртэл үзнэ үү.
              </p>
            )}
          </div>
        ) : alreadyPassed ? (
          <div className='py-6 text-center'>
            <CheckCircle2 className='h-12 w-12 text-green-500 mx-auto mb-3' />
            <p>Та энэ шалгалтанд тэнцсэн байна.</p>
          </div>
        ) : (
          <div className='space-y-6'>
            {sortedQuestions.map((q, idx) => (
              <div key={q.id} className='space-y-3'>
                <div className='font-medium'>
                  {idx + 1}. {q.prompt}
                </div>
                <RadioGroup
                  value={answers[q.id] || ''}
                  onValueChange={(v) => handleSelect(q.id, v)}
                >
                  {q.options.map((opt) => (
                    <div
                      key={opt.id}
                      className='flex items-center space-x-2 border rounded-md p-3 hover:bg-muted/40 cursor-pointer'
                    >
                      <RadioGroupItem value={opt.id} id={`${q.id}-${opt.id}`} />
                      <Label
                        htmlFor={`${q.id}-${opt.id}`}
                        className='flex-1 cursor-pointer'
                      >
                        {opt.text}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          {result ? (
            <Button onClick={handleClose}>Хаах</Button>
          ) : alreadyPassed ? (
            <Button onClick={handleClose}>Хаах</Button>
          ) : (
            <>
              <Button variant='outline' onClick={handleClose}>
                Цуцлах
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!allAnswered || submitting}
              >
                {submitting && (
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                )}
                Илгээх
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CourseTestDialog;
